/**
 * DataQualityReportPanel — Great Expectations / Pandera Data Quality Report
 *
 * Surfaces the GX/Pandera data-quality suite as a panel on the Data stage.
 * Renders five sections:
 *   (a) Statistical Profile — per-field stats from /profile
 *   (b) Expectations Pass/Fail — tabular list from /expectations + /validate
 *   (c) Cross-Document Validation — results from /cross-validate
 *   (d) Full Report summary from /report
 *
 * Design spec compliance:
 *   - CSS variable tokens ONLY (no literal hex values)
 *   - Near-black surfaces, orange brand accent, green for done
 *   - DM Serif Display / Outfit / DM Mono type stack
 *   - Props interface: every prop declared AND destructured
 */

import React, { useEffect } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  FileCheck,
  BarChart3,
  Scale,
  FileText,
  RefreshCw,
  AlertTriangle,
  Database
} from 'lucide-react';
import {
  useDataQualityReport,
  DataQualityReportState
} from '@/hooks/useDataQualityReport';
import { DataTable } from '@/components/ui/DataTable';
import { EmptyState } from '@/components/ui/EmptyState';
import { FinancialValue } from '@/components/shared/FinancialValue';
import {
  ValidateResult,
  GXValidateResult,
  ProfileResult,
  CrossValidateResult,
  DataQualityReport,
  ExpectationResult,
  ColumnProfile,
  CrossValidationIssue,
  ValidationSummary,
} from '@/lib/api/dataQualityApi';

// ============================================================================
// PROPS INTERFACE — every prop declared
// ============================================================================

export interface DataQualityReportPanelProps {
  /** Claim/case ID to fetch data quality reports for */
  claimId: string;
  /** Optional CSS class for container styling */
  className?: string;
  /** Optional callback when refresh is triggered */
  onRefresh?: () => void;
}

// ============================================================================
// HELPERS
// ============================================================================

function scoreBandColor(score: number | null | undefined, courtReady?: boolean): string {
  if (score === null || score === undefined) return 'var(--color-text-dim)';
  if (courtReady === false || score < 70) return 'var(--color-blocker)';
  if (score >= 90) return 'var(--color-verified)';
  return 'var(--color-text-secondary)';
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/** Status badge using CSS variable tokens — verified for pass, blocker for fail */
interface PassFailBadgeProps {
  success: boolean;
  text: string;
}

function PassFailBadge({ success, text }: PassFailBadgeProps): React.JSX.Element {
  const color = success ? 'var(--color-verified)' : 'var(--color-blocker)';
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-[2px] text-xs font-medium"
      style={{
        backgroundColor: 'transparent',
        color: color,
        border: `1px solid ${color}`,
      }}
    >
      {success ? (
        <CheckCircle className="w-3.5 h-3.5" />
      ) : (
        <XCircle className="w-3.5 h-3.5" />
      )}
      {text}
    </span>
  );
}

/** Grade badge with appropriate styling per design spec */
interface GradeBadgeProps {
  grade: 'A' | 'B' | 'C' | 'D' | 'F' | string;
}

function GradeBadge({ grade }: GradeBadgeProps): React.JSX.Element {
  const g = grade.charAt(0).toUpperCase();
  let color = 'var(--color-blocker)';
  if (g === 'A' || g === 'B') color = 'var(--color-verified)';
  else if (g === 'C') color = 'var(--color-text-secondary)';

  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-[2px] text-sm font-mono font-bold"
      style={{
        backgroundColor: 'transparent',
        color: color,
        border: `1px solid ${color}`,
      }}
    >
      {grade}
    </span>
  );
}

/** Metric card for profile statistics */
interface ProfileMetricCardProps {
  label: string;
  value: string | number;
  subtext?: string;
}

function ProfileMetricCard({ label, value, subtext }: ProfileMetricCardProps): React.JSX.Element {
  return (
    <div
      className="rounded-[2px] p-4"
      style={{
        backgroundColor: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border)',
      }}
    >
      <p
        className="text-xs font-medium uppercase tracking-wide mb-1"
        style={{ color: 'var(--color-text-dim)' }}
      >
        {label}
      </p>
      <p
        className="font-mono text-xl font-bold tabular-nums"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        {value}
      </p>
      {subtext && (
        <p
          className="text-xs mt-1"
          style={{ color: 'var(--color-text-dim)' }}
        >
          {subtext}
        </p>
      )}
    </div>
  );
}

/** Section header with icon */
interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  action?: React.ReactNode;
}

function SectionHeader({ icon, title, action }: SectionHeaderProps): React.JSX.Element {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <div
          className="p-2 rounded-[2px]"
          style={{
            backgroundColor: 'var(--color-bg-elevated)',
            border: '1px solid var(--color-border)',
          }}
        >
          <span style={{ color: 'var(--color-text-dim)' }}>{icon}</span>
        </div>
        <h3
          className="font-semibold"
          style={{
            fontFamily: 'var(--font-heading, "DM Serif Display", Georgia, serif)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {title}
        </h3>
      </div>
      {action}
    </div>
  );
}

// ============================================================================
// SECTION COMPONENTS
// ============================================================================

/** Statistical Profile Section — renders per-field stats from /profile */
interface StatisticalProfileSectionProps {
  profile: ProfileResult | null;
}

function StatisticalProfileSection({ profile }: StatisticalProfileSectionProps): React.JSX.Element {
  if (!profile) {
    return (
      <EmptyState
        icon={<BarChart3 className="w-8 h-8" style={{ color: 'var(--color-text-dim)' }} />}
        title="No Profile Data"
        description="Statistical profiling data is not available for this claim."
      />
    );
  }

  const columnEntries = profile.columns ? Object.entries(profile.columns) : [];

  return (
    <div
      className="rounded-[2px] p-5"
      style={{
        backgroundColor: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border)',
      }}
    >
      <SectionHeader
        icon={<BarChart3 className="w-5 h-5" />}
        title="Statistical Profile"
      />

      {/* Summary metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <ProfileMetricCard
          label="Total Rows"
          value={profile.row_count?.toLocaleString() ?? '—'}
        />
        <ProfileMetricCard
          label="Columns"
          value={profile.column_count ?? '—'}
        />
        <ProfileMetricCard
          label="Anomalies"
          value={profile.total_anomalies ?? 0}
          subtext={`${profile.high_severity_anomalies ?? 0} high severity`}
        />
        <ProfileMetricCard
          label="Memory"
          value={`${(profile.memory_size_mb ?? 0).toFixed(2)} MB`}
        />
      </div>

      {/* Column details */}
      {columnEntries.length > 0 && (
        <div className="space-y-3">
          <h4
            className="text-sm font-medium"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Column Statistics
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
                  <th className="text-left py-2 px-2" style={{ color: 'var(--color-text-dim)' }}>Column</th>
                  <th className="text-left py-2 px-2" style={{ color: 'var(--color-text-dim)' }}>Type</th>
                  <th className="text-right py-2 px-2" style={{ color: 'var(--color-text-dim)' }}>Count</th>
                  <th className="text-right py-2 px-2" style={{ color: 'var(--color-text-dim)' }}>Missing</th>
                  <th className="text-right py-2 px-2" style={{ color: 'var(--color-text-dim)' }}>Mean</th>
                </tr>
              </thead>
              <tbody>
                {columnEntries.slice(0, 6).map(([name, col]: [string, ColumnProfile]) => (
                  <tr
                    key={name}
                    style={{ borderBottom: '1px solid var(--color-hairline)' }}
                  >
                    <td
                      className="py-2 px-2 font-mono"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {name}
                    </td>
                    <td className="py-2 px-2" style={{ color: 'var(--color-text-secondary)' }}>
                      {col.type}
                    </td>
                    <td
                      className="py-2 px-2 text-right font-mono tabular-nums"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {col.count?.toLocaleString()}
                    </td>
                    <td
                      className="py-2 px-2 text-right font-mono tabular-nums"
                      style={{
                        color: (col.missing_pct ?? 0) > 5
                          ? 'var(--color-blocker)'
                          : 'var(--color-text-secondary)'
                      }}
                    >
                      {col.missing?.toLocaleString()}
                      ({(col.missing_pct ?? 0).toFixed(1)}%)
                    </td>
                    <td
                      className="py-2 px-2 text-right font-mono tabular-nums"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {col.mean !== undefined ? col.mean.toFixed(2) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {columnEntries.length > 6 && (
              <p
                className="text-xs mt-2 text-center"
                style={{ color: 'var(--color-text-dim)' }}
              >
                +{columnEntries.length - 6} more columns
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Expectations Section — tabular list of pass/fail expectations */
interface ExpectationsSectionProps {
  expectations: GXValidateResult | null;
  validation: ValidateResult | null;
}

function ExpectationsSection({ expectations, validation }: ExpectationsSectionProps): React.JSX.Element {
  if (!expectations && !validation) {
    return (
      <EmptyState
        icon={<FileCheck className="w-8 h-8" style={{ color: 'var(--color-text-dim)' }} />}
        title="No Expectations Data"
        description="Great Expectations validation data is not available."
      />
    );
  }

  const expResults = expectations?.results ?? [];
  const passedCount = expectations?.passed_expectations ?? 0;
  const failedCount = expectations?.failed_expectations ?? 0;
  const totalCount = expectations?.total_expectations ?? 0;
  const score = expectations?.quality_score ?? validation?.quality_score;

  return (
    <div
      className="rounded-[2px] p-5"
      style={{
        backgroundColor: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border)',
      }}
    >
      <SectionHeader
        icon={<FileCheck className="w-5 h-5" />}
        title="Expectations Validation"
        action={expectations?.quality_grade && (
          <GradeBadge grade={expectations.quality_grade} />
        )}
      />

      {/* Summary */}
      <div
        className="flex items-center gap-4 mb-4 p-3 rounded-[2px]"
        style={{ backgroundColor: 'var(--color-bg-elevated)' }}
      >
        <div className="flex items-center gap-2">
          <CheckCircle className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
          <span className="text-sm font-mono tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
            {passedCount} passed
          </span>
        </div>
        <div className="flex items-center gap-2">
          <XCircle className="w-4 h-4" style={{ color: 'var(--color-blocker)' }} />
          <span className="text-sm font-mono tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
            {failedCount} failed
          </span>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span
            className="font-mono text-lg font-bold tabular-nums"
            style={{ color: scoreBandColor(score) }}
          >
            {score === null || score === undefined ? '—' : `${score}%`}
          </span>
          <span className="text-sm" style={{ color: 'var(--color-text-dim)' }}>
            score
          </span>
        </div>
      </div>

      {/* Expectations table */}
      {expResults.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
                <th className="text-left py-2 px-2" style={{ color: 'var(--color-text-dim)' }}>Status</th>
                <th className="text-left py-2 px-2" style={{ color: 'var(--color-text-dim)' }}>Expectation</th>
                <th className="text-left py-2 px-2" style={{ color: 'var(--color-text-dim)' }}>Column</th>
                <th className="text-right py-2 px-2" style={{ color: 'var(--color-text-dim)' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {expResults.slice(0, 10).map((result: ExpectationResult, idx: number) => (
                <tr
                  key={idx}
                  style={{ borderBottom: '1px solid var(--color-hairline)' }}
                >
                  <td className="py-2 px-2">
                    <PassFailBadge
                      success={result.success}
                      text={result.success ? 'Pass' : 'Fail'}
                    />
                  </td>
                  <td
                    className="py-2 px-2 text-xs"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {result.expectation_type}
                  </td>
                  <td
                    className="py-2 px-2 font-mono text-xs"
                    style={{ color: 'var(--color-text-dim)' }}
                  >
                    {result.column || '—'}
                  </td>
                  <td
                    className="py-2 px-2 text-right text-xs"
                    style={{ color: 'var(--color-text-dim)' }}
                  >
                    {result.unexpected_count !== undefined
                      ? `${result.unexpected_count} unexpected`
                      : result.observed_value !== undefined
                        ? String(result.observed_value).slice(0, 20)
                        : '—'
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {expResults.length > 10 && (
            <p
              className="text-xs mt-2 text-center"
              style={{ color: 'var(--color-text-dim)' }}
            >
              +{expResults.length - 10} more expectations
            </p>
          )}
        </div>
      )}

      {/* Validation summary if available */}
      {validation && (
        <div
          className="mt-4 p-3 rounded-[2px]"
          style={{ backgroundColor: 'var(--color-bg-elevated)' }}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Schema Validation
            </span>
            <PassFailBadge
              success={validation.is_valid}
              text={validation.is_valid ? 'Valid' : 'Invalid'}
            />
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs" style={{ color: 'var(--color-text-dim)' }}>
            <span className="font-mono tabular-nums">{validation.total_rows?.toLocaleString()}</span>
            <span>rows</span>
            <span className="font-mono tabular-nums">{validation.valid_rows?.toLocaleString()}</span>
            <span>valid</span>
            {validation.error_count > 0 && (
              <span style={{ color: 'var(--color-blocker)' }}>
                <span className="font-mono tabular-nums">{validation.error_count}</span> errors
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Cross-Document Validation Section */
interface CrossValidationSectionProps {
  crossValidation: CrossValidateResult | null;
}

function CrossValidationSection({ crossValidation }: CrossValidationSectionProps): React.JSX.Element {
  if (!crossValidation) {
    return (
      <EmptyState
        icon={<Scale className="w-8 h-8" style={{ color: 'var(--color-text-dim)' }} />}
        title="No Cross-Validation Data"
        description="Cross-document validation requires multiple uploaded documents."
      />
    );
  }

  const issues = crossValidation.issues ?? [];
  const severityCount = crossValidation.issues_by_severity ?? { critical: 0, high: 0, medium: 0, low: 0 };

  const columns = [
    { key: 'severity', label: 'Severity' },
    { key: 'check', label: 'Check' },
    { key: 'message', label: 'Issue' },
  ] as const;

  return (
    <div
      className="rounded-[2px] p-5"
      style={{
        backgroundColor: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border)',
      }}
    >
      <SectionHeader
        icon={<Scale className="w-5 h-5" />}
        title="Cross-Document Validation"
        action={
          <span
            className="text-sm font-mono tabular-nums"
            style={{ color: 'var(--color-text-dim)' }}
          >
            {crossValidation.documents_checked} documents
          </span>
        }
      />

      {/* Severity summary */}
      <div className="flex flex-wrap gap-2 mb-4">
        {(['critical', 'high', 'medium', 'low'] as const).map((sev) => {
          const count = severityCount[sev] ?? 0;
          let figureColor = 'var(--color-text-dim)';
          if (count > 0) {
            if (sev === 'critical' || sev === 'high') figureColor = 'var(--color-blocker)';
            else figureColor = 'var(--color-text-secondary)';
          }
          return (
            <span
              key={sev}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-[2px] text-xs"
              style={{
                backgroundColor: 'var(--color-bg-elevated)',
                border: '1px solid var(--color-border)',
              }}
            >
              <span className="font-mono tabular-nums font-bold" style={{ color: figureColor }}>
                {count}
              </span>
              <span style={{ color: 'var(--color-text-dim)', textTransform: 'capitalize' }}>
                {sev}
              </span>
            </span>
          );
        })}
        {issues.length === 0 && (
          <span
            className="px-2 py-1 rounded-[2px] text-xs font-medium"
            style={{
              backgroundColor: 'var(--color-bg-elevated)',
              color: 'var(--color-verified)',
              border: '1px solid var(--color-border)',
            }}
          >
            No issues found
          </span>
        )}
      </div>

      {/* Checks performed */}
      {crossValidation.checks_performed?.length > 0 && (
        <div className="mb-4">
          <p
            className="text-xs uppercase tracking-wide mb-2"
            style={{ color: 'var(--color-text-dim)' }}
          >
            Checks Performed
          </p>
          <div className="flex flex-wrap gap-1.5">
            {crossValidation.checks_performed.map((check: string) => (
              <span
                key={check}
                className="px-2 py-0.5 rounded-[2px] text-xs"
                style={{
                  backgroundColor: 'var(--color-bg-elevated)',
                  color: 'var(--color-text-dim)',
                }}
              >
                {check.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Issues table */}
      {issues.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border-sub)' }}>
                <th className="text-left py-2 px-2" style={{ color: 'var(--color-text-dim)' }}>Severity</th>
                <th className="text-left py-2 px-2" style={{ color: 'var(--color-text-dim)' }}>Check</th>
                <th className="text-left py-2 px-2" style={{ color: 'var(--color-text-dim)' }}>Message</th>
              </tr>
            </thead>
            <tbody>
              {issues.slice(0, 8).map((issue: CrossValidationIssue, idx: number) => {
                const isAdverse = issue.severity === 'critical' || issue.severity === 'high';
                const sevColor = isAdverse ? 'var(--color-blocker)' : 'var(--color-text-secondary)';
                return (
                  <tr
                    key={idx}
                    style={{ borderBottom: '1px solid var(--color-hairline)' }}
                  >
                    <td className="py-2 px-2">
                      <span
                        className="text-xs font-medium px-2 py-0.5 rounded-[2px]"
                        style={{
                          backgroundColor: 'transparent',
                          color: sevColor,
                          border: `1px solid ${sevColor}`,
                          textTransform: 'capitalize',
                        }}
                      >
                        {issue.severity}
                      </span>
                    </td>
                    <td
                      className="py-2 px-2 text-xs"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {issue.check}
                    </td>
                    <td
                      className="py-2 px-2 text-sm"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {issue.message}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {issues.length > 8 && (
            <p
              className="text-xs mt-2 text-center"
              style={{ color: 'var(--color-text-dim)' }}
            >
              +{issues.length - 8} more issues
            </p>
          )}
        </div>
      )}

      {/* Reconciliation summary */}
      {crossValidation.reconciliation_summary && (
        <div
          className="mt-4 p-3 rounded-[2px]"
          style={{ backgroundColor: 'var(--color-bg-elevated)' }}
        >
          <p
            className="text-xs uppercase tracking-wide mb-2"
            style={{ color: 'var(--color-text-dim)' }}
          >
            Reconciliation
          </p>
          {crossValidation.reconciliation_summary.total_discrepancy !== undefined && (
            <div className="flex justify-between items-center">
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Total Discrepancy
              </span>
              <span
                className="font-mono tabular-nums"
                style={{
                  color: crossValidation.reconciliation_summary.total_discrepancy !== 0
                    ? 'var(--color-blocker)'
                    : 'var(--color-verified)'
                }}
              >
                <FinancialValue value={Math.abs(crossValidation.reconciliation_summary.total_discrepancy)} />
              </span>
            </div>
          )}
          {crossValidation.reconciliation_summary.note && (
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--color-text-dim)' }}
            >
              {crossValidation.reconciliation_summary.note}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Full Report Summary Section */
interface ReportSummarySectionProps {
  report: DataQualityReport | null;
}

function ReportSummarySection({ report }: ReportSummarySectionProps): React.JSX.Element {
  if (!report) {
    return (
      <EmptyState
        icon={<FileText className="w-8 h-8" style={{ color: 'var(--color-text-dim)' }} />}
        title="No Quality Report"
        description="Comprehensive quality report is not available for this claim."
      />
    );
  }

  const summaries = report.validation_summaries ?? [];
  const recommendations = report.recommendations ?? [];
  const severityCount = report.issues_by_severity ?? { critical: 0, high: 0, medium: 0, low: 0 };

  return (
    <div
      className="rounded-[2px] p-5"
      style={{
        backgroundColor: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border)',
      }}
    >
      <SectionHeader
        icon={<FileText className="w-5 h-5" />}
        title="Quality Report Summary"
        action={report.overall_grade && (
          <GradeBadge grade={report.overall_grade} />
        )}
      />

      {/* Overall score and court-ready */}
      <div
        className="flex items-center justify-between mb-4 p-3 rounded-[2px]"
        style={{ backgroundColor: 'var(--color-bg-elevated)' }}
      >
        <div>
          <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--color-text-dim)' }}>
            Overall Score
          </p>
          <p
            className="font-mono text-2xl font-bold tabular-nums"
            style={{ color: scoreBandColor(report.overall_score, report.is_court_ready) }}
          >
            {report.overall_score === null || report.overall_score === undefined ? '—' : `${report.overall_score}%`}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--color-text-dim)' }}>
            Court Ready
          </p>
          <div className="flex items-center gap-1.5 justify-end mt-1">
            {report.is_court_ready === true ? (
              <>
                <CheckCircle className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
                <span style={{ color: 'var(--color-verified)' }}>Yes</span>
              </>
            ) : report.is_court_ready === false ? (
              <>
                <XCircle className="w-4 h-4" style={{ color: 'var(--color-blocker)' }} />
                <span style={{ color: 'var(--color-blocker)' }}>No</span>
              </>
            ) : (
              <span style={{ color: 'var(--color-text-dim)' }}>—</span>
            )}
          </div>
        </div>
      </div>

      {/* Executive summary */}
      {report.executive_summary && (
        <div className="mb-4">
          <p
            className="text-xs uppercase tracking-wide mb-2"
            style={{ color: 'var(--color-text-dim)' }}
          >
            Executive Summary
          </p>
          <p
            className="text-sm leading-relaxed"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {report.executive_summary}
          </p>
        </div>
      )}

      {/* Issues by severity */}
      <div className="mb-4">
        <p
          className="text-xs uppercase tracking-wide mb-2"
          style={{ color: 'var(--color-text-dim)' }}
        >
          Issues by Severity
        </p>
        <div className="grid grid-cols-4 gap-2">
          {(['critical', 'high', 'medium', 'low'] as const).map((severity) => {
            const count = severityCount[severity] ?? 0;
            let figureColor = 'var(--color-text-dim)';
            if (count > 0) {
              if (severity === 'critical' || severity === 'high') figureColor = 'var(--color-blocker)';
              else figureColor = 'var(--color-text-secondary)';
            }
            return (
              <div
                key={severity}
                className="text-center p-2 rounded-[2px]"
                style={{ backgroundColor: 'var(--color-bg-elevated)' }}
              >
                <p
                  className="font-mono text-lg font-bold tabular-nums"
                  style={{ color: figureColor }}
                >
                  {count}
                </p>
                <p
                  className="text-xs capitalize"
                  style={{ color: 'var(--color-text-dim)' }}
                >
                  {severity}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Validation summaries */}
      {summaries.length > 0 && (
        <div className="mb-4">
          <p
            className="text-xs uppercase tracking-wide mb-2"
            style={{ color: 'var(--color-text-dim)' }}
          >
            Validation Sources
          </p>
          <div className="space-y-2">
            {summaries.map((summary: ValidationSummary, idx: number) => {
              const s = summary.score;
              let scoreColor = 'var(--color-blocker)';
              if (s >= 90) scoreColor = 'var(--color-verified)';
              else if (s >= 70) scoreColor = 'var(--color-text-secondary)';
              return (
                <div
                  key={idx}
                  className="flex items-center justify-between p-2 rounded-[2px]"
                  style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                >
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                      {summary.source}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--color-text-dim)' }}>
                      <span className="font-mono tabular-nums">{summary.passed_checks}</span>/
                      <span className="font-mono tabular-nums">{summary.total_checks}</span> checks passed
                    </p>
                  </div>
                  <div className="text-right">
                    <p
                      className="font-mono font-bold tabular-nums"
                      style={{ color: scoreColor }}
                    >
                      {s}%
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div>
          <p
            className="text-xs uppercase tracking-wide mb-2"
            style={{ color: 'var(--color-text-dim)' }}
          >
            Recommendations
          </p>
          <ul className="space-y-1">
            {recommendations.slice(0, 5).map((rec: string, idx: number) => (
              <li
                key={idx}
                className="flex items-start gap-2 text-sm"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-text-dim)' }} />
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * DataQualityReportPanel — Main component that wires the hook and renders all sections.
 *
 * Every prop from DataQualityReportPanelProps is destructured in the body (tsc-clean requirement).
 */
export function DataQualityReportPanel({
  claimId,
  className,
  onRefresh,
}: DataQualityReportPanelProps): React.JSX.Element {
  const {
    profile,
    expectations,
    validation,
    crossValidation,
    report,
    isLoading,
    error,
    fetchAll,
    refetch,
  } = useDataQualityReport();

  // Fetch data on mount when claimId changes
  useEffect(() => {
    if (claimId) {
      fetchAll(claimId);
    }
  }, [claimId, fetchAll]);

  const handleRefresh = () => {
    if (claimId) {
      refetch(claimId);
      onRefresh?.();
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        className={`rounded-[2px] p-8 flex items-center justify-center ${className ?? ''}`}
        style={{
          backgroundColor: 'var(--color-bg-surface)',
          border: '1px solid var(--color-border)',
        }}
      >
        <RefreshCw
          className="w-8 h-8 animate-spin"
          style={{ color: 'var(--color-brand)' }}
        />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <EmptyState
        icon={<AlertTriangle className="w-12 h-12" style={{ color: 'var(--color-blocker)' }} />}
        title="Error Loading Data Quality Report"
        description={error.message || 'Failed to load data quality information.'}
        action={
          <button
            onClick={handleRefresh}
            className="px-4 py-2 rounded-[2px] text-sm font-medium transition-colors"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-border)',
            }}
          >
            Retry
          </button>
        }
      />
    );
  }

  // Render the full panel with all sections
  return (
    <div className={`space-y-6 ${className ?? ''}`}>
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="w-6 h-6" style={{ color: 'var(--color-text-dim)' }} />
          <h2
            className="text-xl font-bold"
            style={{
              fontFamily: 'var(--font-heading, "DM Serif Display", Georgia, serif)',
              color: 'var(--color-text-secondary)',
            }}
          >
            Data Quality Report
          </h2>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-3 py-1.5 rounded-[2px] text-sm transition-colors"
          style={{
            backgroundColor: 'transparent',
            color: 'var(--color-text-secondary)',
            border: '1px solid var(--color-border)',
          }}
        >
          <RefreshCw className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
          Refresh
        </button>
      </div>

      {/* Report Summary (top) */}
      <ReportSummarySection report={report} />

      {/* Two-column layout for middle sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StatisticalProfileSection profile={profile} />
        <ExpectationsSection expectations={expectations} validation={validation} />
      </div>

      {/* Cross-validation (full width) */}
      <CrossValidationSection crossValidation={crossValidation} />
    </div>
  );
}

export default DataQualityReportPanel;
